// src/views/litellm/components/AiCostBasisStrip.jsx
import React from "react";
import PropTypes from "prop-types";
import { eur, aiCostBasis } from "../format";

// Figure is one labelled amount in the strip's inline row.
function Figure({ label, value, hint, strong }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3">
        {label}
        {hint ? (
          <span
            tabIndex={0}
            title={hint}
            aria-label={hint}
            className="cursor-help rounded-full border border-card-bd px-1 text-[9px] leading-tight text-text-3"
          >
            ?
          </span>
        ) : null}
      </div>
      <div className={`mt-0.5 text-lg tabular-nums ${strong ? "font-semibold text-text" : "text-text-2"}`}>{value}</div>
    </div>
  );
}

// AiCostBasisStrip is the month-to-date re-basis proof, as a compact full-width strip: actual
// (re-based) cost, the API-equivalent value it's measured against, and the modelled saving between
// them. It replaces the old lone half-width card (which left a large empty gutter) and reframes the
// headline number as a *saving vs API-equivalent pricing* rather than an "effective discount",
// which could be misread as a contracted vendor discount. The caption defines "API-equivalent" and
// explains why today's figures are USD (the LiteLLM per-call meter) while this basis is EUR (your
// subscription) — so the two currencies on the page don't read as an inconsistency.
export default function AiCostBasisStrip({ aiCost, loading }) {
  const basis = aiCostBasis(aiCost);

  return (
    <div className="rounded-card border border-card-bd bg-card p-5">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-3">AI cost basis</div>
        <div className="text-[11px] text-text-3">this month</div>
      </div>

      {loading ? (
        <div className="h-6 w-64 animate-pulse rounded bg-surface-2" />
      ) : !basis ? (
        <div className="text-sm text-text-3">No AI usage this period.</div>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
            <Figure label="Actual" value={eur(basis.actual)} />
            <Figure
              label="API-equivalent"
              value={eur(basis.shadow)}
              hint="What these calls would cost at standard provider API list prices."
            />
            <Figure label="Estimated savings" value={`${eur(basis.savings)} (${basis.pct}%)`} strong />
          </div>
          <p className="mt-3 text-[11px] leading-snug text-text-3">
            Estimated saving of a flat subscription versus API-equivalent pricing. Today's usage above is
            metered by LiteLLM in USD; this basis reflects your EUR subscription, so the two aren't directly
            additive.
          </p>
        </>
      )}
    </div>
  );
}

AiCostBasisStrip.propTypes = {
  aiCost: PropTypes.object,
  loading: PropTypes.bool,
};
